import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import DealProduct from '../../../../interfaces/deal-product';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import Farm from '../../../../interfaces/farm';
import AddPartnerModal from './modals/add-partner-modal';
import { UseFormReturn } from 'react-hook-form';
import DealType from '../../../../enums/deal-type';

const PRODUCT_OBTAIN_REQUIRED = [
  DealType.sold,
  DealType.moved,
  DealType.self_consumed,
  DealType.spent,
  DealType.lost,
];

interface Warehouse {
  farm?: Farm;
  isOpen: boolean;
  openModal: boolean;
  isWarehouseModalOpen: boolean;
  isOpenUsedProducts: boolean;
  isPartnerRequired: boolean;
  isTargetUsageRequired: boolean;
  PRODUCT_OBTAIN_REQUIRED: DealType[];
  setIsOpen: (value: boolean) => void;
  setOpenModal: (value: boolean) => void;
  setIsWarehouseModalOpen: (value: boolean) => void;
  setIsOpenUsedProducts: (value: boolean) => void;
  setIsPartnerRequired: (value: boolean) => void;
  setIsTargetUsageRequired: (value: boolean) => void;
  formValues: DealProduct | undefined;
  setFormValues: (value: DealProduct) => void;
  exceptionProducts: string;
  setExceptionProducts: (value: string) => void;
  modalAddPartner: null | React.JSX.Element;
  openModalAddPartner: () => void;
  closeModalAddPartner: () => void;
  formMethods?: UseFormReturn;
  setFormMethods: (methods: UseFormReturn) => void;
}

const initialWarehouseValue: Warehouse = {
  farm: undefined,
  isOpen: false,
  openModal: false,
  isWarehouseModalOpen: false,
  isOpenUsedProducts: false,
  exceptionProducts: '',
  isPartnerRequired: false,
  isTargetUsageRequired: false,
  PRODUCT_OBTAIN_REQUIRED: PRODUCT_OBTAIN_REQUIRED,
  setExceptionProducts: () => {},
  setIsOpen: () => {},
  setOpenModal: () => {},
  setIsWarehouseModalOpen: () => {},
  setIsOpenUsedProducts: () => {},
  setIsPartnerRequired: () => {},
  setIsTargetUsageRequired: () => {},
  formValues: undefined,
  setFormValues: () => {},
  openModalAddPartner: () => {},
  closeModalAddPartner: () => {},
  modalAddPartner: null,
  formMethods: undefined,
  setFormMethods: () => {},
};

const WarehouseContext = createContext<Warehouse>(initialWarehouseValue);
export const useWarehouseContext = () => useContext(WarehouseContext);

interface Props {
  children: ReactNode;
}

export const WarehouseProvider = ({ children }: Props) => {
  const { farmUser: farm } = useSelector((state: RootState) => ({
    farmUser: state.global.farm,
  }));

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] =
    useState<boolean>(false);
  const [isOpenUsedProducts, setIsOpenUsedProducts] = useState<boolean>(false);
  const [exceptionProducts, setExceptionProducts] = useState<string>('');
  const [formValues, setFormValues] = useState<DealProduct>();
  const [isPartnerRequired, setIsPartnerRequired] = useState<boolean>(false);
  const [isTargetUsageRequired, setIsTargetUsageRequired] =
    useState<boolean>(false);
  const [modalAddPartner, setModalAddPartner] =
    useState<null | React.JSX.Element>(null);
  const [formMethods, setFormMethods] = useState<UseFormReturn | undefined>(
    undefined
  );

  const close = useCallback(() => setModalAddPartner(null), []);

  const memoizedModal = useMemo(
    () => <AddPartnerModal onClose={close} />,
    [close]
  );

  const openModalAddPartner = useCallback(() => {
    if (modalAddPartner === null) setModalAddPartner(memoizedModal);
  }, [modalAddPartner, memoizedModal]);

  const closeModalAddPartner = useCallback(() => {
    if (modalAddPartner !== null) setModalAddPartner(null);
  }, [modalAddPartner]);

  return (
    <WarehouseContext.Provider
      value={{
        farm,
        isOpen,
        openModal,
        isOpenUsedProducts,
        PRODUCT_OBTAIN_REQUIRED,
        setIsOpen,
        setOpenModal,
        setIsOpenUsedProducts,
        formValues,
        setFormValues,
        isPartnerRequired,
        setIsPartnerRequired,
        isTargetUsageRequired,
        setIsTargetUsageRequired,
        exceptionProducts,
        setExceptionProducts,
        openModalAddPartner,
        closeModalAddPartner,
        modalAddPartner,
        isWarehouseModalOpen,
        setIsWarehouseModalOpen,
        formMethods,
        setFormMethods,
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
};
